import { useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import { ArrowLeft, Users, CalendarCheck, Megaphone, FolderDown, FileText, Link2, Pin, Music, ChevronRight } from 'lucide-react'
import { useCollection, useDocument } from '../../hooks/useFirestore'
import { formatDate, groupBy, todayISO } from '../../utils/helpers'
import { Card, PageLoader, Badge } from '../../components/common'

const DepartmentPage = () => {
  const { slug } = useParams()
  const { data: department, loading } = useDocument('departments', slug)

  const { data: members } = useCollection(`departments/${slug}/members`, {
    orderByField: 'displayName',
    orderDirection: 'asc',
  })

  const { data: assignments } = useCollection('assignments', {
    orderByField: 'date',
    orderDirection: 'asc',
    whereClauses: [
      { field: 'departmentId', operator: '==', value: slug },
      { field: 'date', operator: '>=', value: todayISO() },
    ],
  })

  const { data: announcements } = useCollection('announcements', {
    orderByField: 'publishedAt',
    orderDirection: 'desc',
    whereClause: { field: 'isPublished', operator: '==', value: true },
  })

  const { data: nextSetlists } = useCollection('setlists', {
    orderByField: 'serviceDate',
    orderDirection: 'asc',
    limitCount: 1,
    whereClauses: [
      { field: 'departmentId', operator: '==', value: slug },
      { field: 'status', operator: '==', value: 'published' },
      { field: 'serviceDate', operator: '>=', value: todayISO() },
    ],
  })
  const nextSetlist = nextSetlists?.[0]

  const { data: resources } = useCollection('resources')

  const deptAnnouncements = useMemo(
    () => (announcements || []).filter((a) => (a.departmentIds || []).includes(slug)),
    [announcements, slug]
  )
  const deptResources = useMemo(
    () => (resources || []).filter((r) => (r.departmentIds || []).includes(slug)),
    [resources, slug]
  )

  const byDate = useMemo(() => groupBy(assignments || [], 'date'), [assignments])
  const dates = Object.keys(byDate).sort()

  if (loading) return <PageLoader message="Loading department..." />

  if (!department) {
    return (
      <Card className="text-center py-12 text-gray-500">
        <p>Department not found.</p>
        <Link to="/workers" className="text-primary-600 hover:text-primary-700 font-medium">
          Back to dashboard
        </Link>
      </Card>
    )
  }

  return (
    <div className="space-y-8">
      <div>
        <Link
          to="/workers"
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Dashboard
        </Link>
        <h1 className="text-2xl font-heading font-bold text-dark-100">{department.name}</h1>
        {department.leaderName && (
          <p className="text-gray-500">Led by {department.leaderName}</p>
        )}
        {department.description && (
          <p className="text-gray-600 mt-2">{department.description}</p>
        )}
      </div>

      {/* Announcements */}
      {deptAnnouncements.length > 0 && (
        <section>
          <h2 className="font-heading font-semibold text-dark-100 flex items-center gap-2 mb-3">
            <Megaphone className="w-5 h-5 text-primary-600" />
            Announcements
          </h2>
          <div className="space-y-2">
            {deptAnnouncements.map((a) => (
              <div key={a.id} className="bg-white rounded-xl shadow-soft p-4">
                <div className="flex items-center gap-2 mb-1">
                  {a.pinned && <Pin className="w-4 h-4 text-accent-600" />}
                  <p className="font-medium text-dark-100">{a.title}</p>
                </div>
                <div className="text-sm text-gray-600 prose prose-sm max-w-none">
                  <ReactMarkdown>{a.body}</ReactMarkdown>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Next song list */}
      {nextSetlist && (
        <section>
          <h2 className="font-heading font-semibold text-dark-100 flex items-center gap-2 mb-3">
            <Music className="w-5 h-5 text-primary-600" />
            Next Song List — {formatDate(nextSetlist.serviceDate, 'MMMM d')}
          </h2>
          <Link
            to="/workers/songs"
            className="block bg-white rounded-xl shadow-soft p-4 hover:shadow-lg transition-shadow"
          >
            <ol className="space-y-1">
              {(nextSetlist.items || []).map((item, i) => (
                <li key={`${item.songId}-${i}`} className="text-sm text-gray-700">
                  {i + 1}. {item.title}
                  {item.songKey && <span className="text-gray-400"> ({item.songKey})</span>}
                  {item.leadName && (
                    <Badge variant="warning" size="sm" className="ml-2">
                      Lead: {item.leadName}
                    </Badge>
                  )}
                </li>
              ))}
            </ol>
            <p className="text-sm text-primary-600 font-medium mt-2 flex items-center gap-1">
              Open Songs <ChevronRight className="w-4 h-4" />
            </p>
          </Link>
        </section>
      )}

      {/* Upcoming roster */}
      <section>
        <h2 className="font-heading font-semibold text-dark-100 flex items-center gap-2 mb-3">
          <CalendarCheck className="w-5 h-5 text-primary-600" />
          Upcoming Roster
        </h2>
        <div className="space-y-4">
          {dates.map((date) => (
            <div key={date} className="bg-white rounded-xl shadow-soft overflow-hidden">
              <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
                <p className="font-heading font-semibold text-dark-100">
                  {formatDate(date, 'EEEE, MMMM d, yyyy')}
                </p>
              </div>
              <div className="divide-y divide-gray-100">
                {byDate[date].map((a) => (
                  <div key={a.id} className="px-5 py-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="font-medium text-dark-100">{a.workerName}</p>
                      <p className="text-sm text-gray-600">
                        {[a.serviceName, a.serviceTime, a.duty].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {dates.length === 0 && (
            <Card className="text-center py-8 text-gray-500">
              Nothing scheduled yet
            </Card>
          )}
        </div>
      </section>

      {/* Resources */}
      {deptResources.length > 0 && (
        <section>
          <h2 className="font-heading font-semibold text-dark-100 flex items-center gap-2 mb-3">
            <FolderDown className="w-5 h-5 text-primary-600" />
            Resources
          </h2>
          <div className="space-y-2">
            {deptResources.map((r) => (
              <a
                key={r.id}
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-xl shadow-soft p-4 flex items-center gap-3 hover:shadow-lg transition-shadow"
              >
                {r.type === 'link'
                  ? <Link2 className="w-5 h-5 text-primary-600 flex-shrink-0" />
                  : <FileText className="w-5 h-5 text-primary-600 flex-shrink-0" />}
                <div>
                  <p className="font-medium text-dark-100">{r.title}</p>
                  {r.description && <p className="text-sm text-gray-500">{r.description}</p>}
                </div>
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Members */}
      <section>
        <h2 className="font-heading font-semibold text-dark-100 flex items-center gap-2 mb-3">
          <Users className="w-5 h-5 text-primary-600" />
          Members ({(members || []).length})
        </h2>
        <Card>
          {(members || []).length > 0 ? (
            <ul className="grid sm:grid-cols-2 gap-2">
              {(members || []).map((m) => (
                <li key={m.id} className="text-gray-700">{m.displayName}</li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 text-center py-4">No members assigned yet</p>
          )}
        </Card>
      </section>
    </div>
  )
}

export default DepartmentPage
